/**
 * ChallengesTab — Adversarial Deliberation View
 *
 * Displays the adjuster's challenges to the attorney's case,
 * the attorney's rebuttals, and the vulnerability assessment.
 * Allows triggering a new challenge round.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldQuestion,
  Swords,
  Target,
  FileText,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Play,
  XCircle,
} from 'lucide-react';
import {
  type ChallengeRoundResult,
  type Challenge,
  type Rebuttal,
  type VulnerabilityAssessment,
  triggerChallengeRound,
  getChallengeRound,
  getChallengeRoundStatus,
  formatCurrency,
} from '@/services/deliberationApi';

// =============================================================================
// PROPS
// =============================================================================

interface ChallengesTabProps {
  reportId: string;
  /** Increment to force a refresh from the parent (e.g. header Refresh button). */
  refreshTrigger?: number;
}

// =============================================================================
// HELPERS
// =============================================================================

const severityColors: Record<string, React.CSSProperties> = {
  critical: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  high: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  medium: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
  low: { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' },
};

const outcomeConfig: Record<string, { icon: React.ReactNode; color: React.CSSProperties; label: string }> = {
  countered: {
    icon: <ShieldCheck className="h-4 w-4" />,
    color: { color: 'var(--color-verified)', backgroundColor: 'transparent', borderColor: 'var(--color-verified)' },
    label: 'Countered',
  },
  acknowledged: {
    icon: <ShieldAlert className="h-4 w-4" />,
    color: { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    label: 'Acknowledged Vulnerability',
  },
  partially_addressed: {
    icon: <ShieldQuestion className="h-4 w-4" />,
    color: { color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' },
    label: 'Partially Addressed',
  },
};

function CaseStrengthMeter({ score }: { score: number }) {
  const color =
    score >= 75 ? 'var(--color-verified)' :
    score >= 50 ? 'var(--color-brand)' :
    score >= 25 ? 'var(--color-brand)' : 'var(--color-brand)';

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Case Strength</span>
        <span className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>{score}/100</span>
      </div>
      <div className="w-full rounded-full h-3" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <div
          className="h-3 rounded-full transition-all duration-500"
          style={{ width: `${score}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function ChallengesTab({ reportId, refreshTrigger = 0 }: ChallengesTabProps) {
  const [result, setResult] = useState<ChallengeRoundResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedChallenge, setExpandedChallenge] = useState<string | null>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  // Ref so startPolling can call fetchData without a circular dep.
  const fetchDataRef = useRef<(() => Promise<void>) | null>(null);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getChallengeRoundStatus(reportId);
        if (status.status === 'complete') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          fetchDataRef.current?.();
        } else if (status.status === 'failed') {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          setRunning(false);
          setError(status.error || 'Challenge round failed');
        }
      } catch {
        // keep polling
      }
    }, 5000);
  }, [reportId]);

  const fetchData = useCallback(async () => {
    try {
      const data = await getChallengeRound(reportId);
      setResult(data);
      setError(null);
      setRunning(false);
    } catch (err: any) {
      // The 404 path is the canonical empty state — the backend returns
      // `detail="No challenge round found for {id}."` for every case
      // until a round is triggered. The api utility surfaces that detail
      // verbatim as `err.message`, so neither "404" nor "not found"
      // appears in it. Match the actual detail string in addition to the
      // legacy substrings so the empty-state CTA renders without an
      // inline error blob (PR #1.7 follow-up — see
      // `frontend/e2e/deliberation-challenges-empty-state.spec.ts`).
      const message: string = err?.message || '';
      const isEmptyState =
        message.includes('404') ||
        message.includes('not found') ||
        /no challenge round found/i.test(message);
      if (isEmptyState) {
        setResult(null);
        setError(null);
        try {
          const status = await getChallengeRoundStatus(reportId);
          if (status.status === 'running') {
            setRunning(true);
            startPolling(); // Resume polling — was missing on page-load resumption
          } else {
            setRunning(false); // Round not running (status reset after redeploy, or never started)
          }
        } catch {
          setRunning(false); // Status check failed — treat as not running
        }
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  }, [reportId, startPolling]);

  // Keep ref in sync so startPolling's closure always calls the latest fetchData.
  fetchDataRef.current = fetchData;

  useEffect(() => {
    fetchData();
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [fetchData]);

  // Re-fetch when the parent header Refresh button is clicked.
  useEffect(() => {
    if (refreshTrigger === 0) return;
    fetchData();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrigger = useCallback(async () => {
    setRunning(true);
    setError(null);
    try {
      await triggerChallengeRound(reportId);
      startPolling();
    } catch (err: any) {
      setRunning(false);
      setError(err.message);
    }
  }, [reportId, startPolling]);

  // Get rebuttal for a challenge
  const getRebuttal = (challengeId: string): Rebuttal | undefined => {
    return result?.rebuttals.find(r => r.challenge_id === challengeId);
  };

  // =========================================================================
  // LOADING / EMPTY / RUNNING
  // =========================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
      </div>
    );
  }

  if (running) {
    return (
      <div className="text-center py-12">
        <Swords className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: 'var(--color-brand)' }} />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          Adversarial Deliberation In Progress
        </h3>
        <p className="text-sm mb-2" style={{ color: 'var(--color-text-secondary)' }}>
          The adjuster is stress-testing the attorney's case...
        </p>
        <p className="text-xs mb-4" style={{ color: 'var(--color-text-dim)' }}>
          This typically takes 60-120 seconds. The page will auto-refresh.
        </p>
        <button
          onClick={fetchData}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors"
          style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          Check for Results
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="text-center py-12">
        <Swords className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--color-text-ghost)' }} />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
          No Challenge Round Yet
        </h3>
        <p className="text-sm mb-6 max-w-md mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
          Run the adversarial challenge round to have the adjuster stress-test the attorney's case.
          Every weakness will be identified with specific rebuttals and action items.
        </p>
        {error && (
          <div className="mb-4 p-3 border rounded-lg text-sm max-w-md mx-auto" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }}>
            {error}
          </div>
        )}
        <button
          onClick={handleTrigger}
          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-lg transition-colors shadow-sm"
          style={{ color: 'var(--color-on-brand)', backgroundColor: 'var(--color-brand)' }}
        >
          <Swords className="h-4 w-4" />
          Start Challenge Round
        </button>
      </div>
    );
  }

  // =========================================================================
  // DATA VIEW
  // =========================================================================

  const vuln = result.vulnerability_assessment;

  return (
    <div className="space-y-6">
      {/* Vulnerability Assessment Summary */}
      {vuln && (
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>Vulnerability Assessment</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <CaseStrengthMeter score={vuln.case_strength_score} />

            <div className="grid grid-cols-2 gap-3">
              <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{vuln.challenges_total}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Total Challenges</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'transparent' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{vuln.challenges_countered}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Countered</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{vuln.challenges_partially_addressed}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Partial</p>
              </div>
              <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{vuln.challenges_acknowledged}</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Unresolved</p>
              </div>
            </div>
          </div>

          {parseFloat(vuln.total_amount_at_risk) > 0 && (
            <div className="mt-4 p-3 border rounded-lg flex items-center gap-3" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <DollarSign className="h-5 w-5 flex-shrink-0" style={{ color: 'var(--color-needs-human-text)' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                  {formatCurrency(vuln.total_amount_at_risk)} at risk from unresolved challenges
                </p>
                <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                  Potential case improvement: {formatCurrency(vuln.estimated_case_improvement)} if all vulnerabilities addressed
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Client Actions Required */}
      {vuln && vuln.client_actions_required.length > 0 && (
        <div className="rounded-xl border p-6" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Client Actions Required ({vuln.client_actions_required.length})
            </h3>
          </div>
          <div className="space-y-2">
            {vuln.client_actions_required.map((action, i) => (
              <div key={i} className="flex items-start gap-3 p-3 border rounded-lg" style={{ borderColor: 'var(--color-border-sub)' }}>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border" style={severityColors[action.urgency] || severityColors.medium}>
                  {action.urgency}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{action.action}</p>
                  {action.dollar_impact && (
                    <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                      Impact: {formatCurrency(action.dollar_impact)}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Challenges + Rebuttals */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <Target className="h-5 w-5" />
          Challenges & Rebuttals ({result.challenges.length})
        </h3>

        {result.challenges.map((challenge) => {
          const rebuttal = getRebuttal(challenge.challenge_id);
          const outcome = rebuttal ? outcomeConfig[rebuttal.outcome] || outcomeConfig.acknowledged : null;
          const isExpanded = expandedChallenge === challenge.challenge_id;

          return (
            <div
              key={challenge.challenge_id}
              className="rounded-xl border overflow-hidden"
              style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}
            >
              {/* Challenge Header */}
              <button
                onClick={() => setExpandedChallenge(isExpanded ? null : challenge.challenge_id)}
                className="w-full px-5 py-4 flex items-center gap-3 text-left transition-colors"
              >
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border" style={severityColors[challenge.severity] || severityColors.medium}>
                  {challenge.severity}
                </span>
                <span className="text-xs uppercase tracking-wide" style={{ color: 'var(--color-text-dim)' }}>
                  {challenge.challenge_type.replace('_', ' ')}
                </span>
                <span className="flex-1 text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                  {challenge.attack_argument.slice(0, 100)}
                  {challenge.attack_argument.length > 100 ? '...' : ''}
                </span>
                {outcome && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border" style={outcome.color}>
                    {outcome.icon}
                    {outcome.label}
                  </span>
                )}
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t px-5 py-4 space-y-4" style={{ borderColor: 'var(--color-border)' }}>
                  {/* Attack Argument */}
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--color-needs-human-text)' }}>
                      Attack Argument
                    </p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{challenge.attack_argument}</p>
                  </div>

                  {/* Evidence */}
                  {challenge.evidence_cited.length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-muted)' }}>
                        Evidence Cited
                      </p>
                      <ul className="list-disc list-inside text-sm space-y-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                        {challenge.evidence_cited.map((e, i) => (
                          <li key={i}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Dollar Impact */}
                  {challenge.dollar_impact && (
                    <div className="flex items-center gap-2 text-sm">
                      <DollarSign className="h-4 w-4" style={{ color: 'var(--color-needs-human-text)' }} />
                      <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                        {formatCurrency(challenge.dollar_impact)} at risk
                      </span>
                    </div>
                  )}

                  {/* Rebuttal */}
                  {rebuttal ? (
                    <div className="p-4 rounded-lg border" style={outcome?.color || { borderColor: 'var(--color-border)' }}>
                      <p className="text-xs font-medium uppercase tracking-wide mb-1 flex items-center gap-1">
                        {outcome?.icon}
                        Attorney's Rebuttal — {outcome?.label}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{rebuttal.counter_argument}</p>

                      {rebuttal.remaining_vulnerability && (
                        <div className="mt-2 p-2 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                          Remaining: {rebuttal.remaining_vulnerability}
                        </div>
                      )}

                      {rebuttal.client_action_needed && (
                        <div className="mt-2 p-2 rounded text-xs" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          <FileText className="h-3 w-3 inline mr-1" />
                          Client action: {rebuttal.client_action_needed}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg text-sm italic" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}>
                      No rebuttal received for this challenge.
                    </div>
                  )}

                  {/* Suggested Counter */}
                  {challenge.suggested_counter && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--color-verified)' }}>
                        How to Defeat This Challenge
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{challenge.suggested_counter}</p>
                    </div>
                  )}

                  {/* Documents Needed */}
                  {challenge.documents_needed.length > 0 && (
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-muted)' }}>
                        Documents Needed
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {challenge.documents_needed.map((doc, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Re-run button */}
      <div className="text-center pt-4">
        <button
          onClick={handleTrigger}
          disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg disabled:opacity-50 transition-colors"
          style={{ color: 'var(--color-needs-human-text)', backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}
        >
          <Swords className="h-4 w-4" />
          {running ? 'Running...' : 'Re-run Challenge Round'}
        </button>
      </div>
    </div>
  );
}
